import React, { useState } from "react";
import { Send, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Newsletter signup form.
 *
 * Props:
 *  - source: free-form analytics tag sent to /api/newsletter (default "footer")
 *  - variant: "dark" (white text on dark bg — for the dark footer) or
 *             "light" (dark text on light bg — for maintenance / coming-soon /
 *             any light section). Default "dark" preserves footer behaviour.
 */
const NewsletterForm = ({ source = "footer", variant = "dark" }) => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/newsletter", { email, source });
            setDone(true);
            toast.success(data.already_subscribed ? "You're already on the list" : "Thanks for subscribing!");
        } catch (err) {
            toast.error("Subscription failed");
        } finally {
            setSubmitting(false);
        }
    };

    const isLight = variant === "light";

    const inputCls = isLight
        ? "flex-1 rounded-full bg-white border border-black/10 px-4 py-2.5 text-sm text-[#1C252E] placeholder-[#4A5568] focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
        : "flex-1 rounded-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 transition-all";

    const successCls = isLight
        ? "bg-green-50 border border-green-300 text-green-800 rounded-full px-5 py-3 inline-flex items-center gap-2 text-sm"
        : "bg-[#E53935]/15 border border-[#E53935]/30 text-white rounded-full px-5 py-3 inline-flex items-center gap-2 text-sm";

    if (done) {
        return (
            <div className={successCls} data-testid="newsletter-success">
                <Check className="w-4 h-4" strokeWidth={2} />
                You're on the list
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-2 w-full max-w-md"
            data-testid="newsletter-form"
            data-variant={variant}
            aria-label="Newsletter signup"
        >
            <label htmlFor={`newsletter-email-${source}`} className="sr-only">Email address</label>
            <input
                id={`newsletter-email-${source}`}
                type="email"
                required
                placeholder="parent@email.com"
                aria-label="Email address"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className={inputCls}
            />
            <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-60 whitespace-nowrap"
            >
                <Send className="w-4 h-4" strokeWidth={2} />
                {submitting ? "…" : "Subscribe"}
            </button>
        </form>
    );
};

export default NewsletterForm;
